import { ReactNode, useEffect, useId, useRef } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}

/**
 * A bottom sheet, for overflow on a phone.
 *
 * Same native `<dialog>` foundation as `Modal` — focus trap, focus restore,
 * top layer, Escape — but pinned to the bottom edge and full width, so its
 * targets land under the thumb rather than at the top of the screen.
 */
export function Sheet({ open, onClose, title, children }: Props) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    if (open && !dialog.open) {
      dialog.showModal();
      document.body.style.overflow = 'hidden';
    } else if (!open && dialog.open) {
      dialog.close();
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onCancel={e => {
        e.preventDefault();
        onClose();
      }}
      onClick={e => {
        if (e.target === ref.current) onClose();
      }}
      // `mt-auto` against the dialog's own `inset: 0` pins it to the bottom;
      // `max-w-none` undoes the centred-modal width.
      className="w-full max-w-none m-0 mt-auto p-0 max-h-[85vh] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-t-2xl shadow-overlay open:flex open:flex-col backdrop:bg-black/50 open:animate-slide-up"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 shrink-0">
        <h2 id={titleId} className="text-sm font-semibold">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          className="-mr-2 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="overflow-y-auto pb-[max(env(safe-area-inset-bottom),0.5rem)]">{children}</div>
    </dialog>
  );
}
